import { Controller } from "@hotwired/stimulus"
import { monitorAction, monitorError, navigationType, postFrontendDiagnostic } from "../lib/datadog_monitoring"

export default class extends Controller {
  static targets = [
    "form",
    "postJobButton",
    "saveDraft",
    "checkLimit",
    "primaryCategory",
    "startDate",
    "noneOfTheAbove",
    "jobTypeCheckbox",
    "toggleLocationLink",
    "locationCheckbox",
    "jobScheduleRadio",
    "seasonRow"
  ]

  connect() {
    this.initializeJobSchedule()
    this.initializePostJobButton()
    this.disablePrimaryCategory()
    this.initializeNoneOfTheAbove()
    this.initializeCheckLimits()
    this.monitorFormInitialization()

    if (window.location.hash === "#post_it") {
      this.scrollToAnchor('#post_it')
    }
  }

  // Form submission - enable primary category checkbox
  submitForm(event) {
    if (this.hasPrimaryCategoryTarget) {
      this.enableCheckbox(this.primaryCategoryTarget)
    }
  }

  // Save draft button click
  saveDraft(event) {
    event.preventDefault()
    if (this.hasStartDateTarget) {
      this.startDateTarget.value = ""
    }
    this.formTarget.submit()
  }

  // Job schedule radio change
  jobScheduleChange(event) {
    const isSeasonal = event.target.value === "seasonal"
    this.seasonRowTargets.forEach(row => {
      row.style.display = isSeasonal ? "" : "none"
    })
  }

  // Toggle location checkboxes (select all/deselect all)
  toggleLocationCheckboxes(event) {
    event.preventDefault()
    const link = event.currentTarget
    const isSelectAll = link.textContent === "Select All"

    this.locationCheckboxTargets.forEach(cb => {
      cb.checked = isSelectAll
    })

    link.textContent = isSelectAll ? "Deselect All" : "Select All"
  }

  // None of the Above checkbox click
  noneOfTheAboveClick(event) {
    if (event.target.checked) {
      this.jobTypeCheckboxTargets.forEach(cb => {
        cb.checked = false
      })
    }
  }

  // Job type checkbox click
  jobTypeCheckboxClick(event) {
    if (event.target.checked && this.hasNoneOfTheAboveTarget) {
      this.noneOfTheAboveTarget.checked = false
    }
  }

  // Check limit change
  checkLimitChange(event) {
    const container = event.currentTarget.closest('[data-job-posts-target="checkLimit"]')
    this.applyCheckLimit(container)
  }

  // Private methods

  monitorFormInitialization() {
    if (!this.hasFormTarget) return
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    const startedAt = performance.now()
    const context = {
      page: "job_posts/form_component",
      pathname: window.location.pathname,
      navigation_type: navigationType()
    }

    monitorAction("job_post.form.connect", context)

    window.requestAnimationFrame(() => {
      this.reportInitializationStatusWithRetry(context, startedAt)
    })
  }

  reportInitializationStatusWithRetry(context, startedAt, attempt = 1) {
    const status = this.currentInitializationStatus(context)
    const initialized = this.criticalComponentsInitialized(status)
    const maxAttempts = 8

    if (initialized || attempt >= maxAttempts) {
      this.reportInitializationStatus(status, {
        attempts: attempt,
        elapsedMs: Math.round(performance.now() - startedAt)
      })
      return
    }

    window.setTimeout(() => {
      this.reportInitializationStatusWithRetry(context, startedAt, attempt + 1)
    }, 250)
  }

  currentInitializationStatus(context) {
    const jobTitleSelectPresent = this.jobTitleSelectPresent()
    const adEditorPresent = this.editorPresent("ad_content")
    const howToApplyEditorPresent = this.editorPresent("how_to_apply_content")

    return {
      ...context,
      tom_select_initialized: this.tomSelectInitialized(),
      photo_picker_initialized: this.photoPickerInitialized(),
      ad_editor_initialized: this.editorInitialized("ad_content"),
      how_to_apply_editor_initialized: this.editorInitialized("how_to_apply_content"),
      job_title_select_present: jobTitleSelectPresent,
      job_title_select_tom_select_controller: this.jobTitleSelectControllerAttached(),
      job_title_should_initialize: jobTitleSelectPresent,
      ad_editor_should_initialize: adEditorPresent,
      how_to_apply_editor_should_initialize: howToApplyEditorPresent,
      rhino_custom_element_defined: this.rhinoCustomElementDefined(),
      ad_editor_present: adEditorPresent,
      how_to_apply_editor_present: howToApplyEditorPresent,
      script_fingerprint: this.scriptFingerprint()
    }
  }

  criticalComponentsInitialized(status) {
    const jobTitleReady = !status.job_title_should_initialize || status.tom_select_initialized
    const adEditorReady = !status.ad_editor_should_initialize || status.ad_editor_initialized
    const howToApplyEditorReady = !status.how_to_apply_editor_should_initialize || status.how_to_apply_editor_initialized

    return jobTitleReady && adEditorReady && howToApplyEditorReady
  }

  reportInitializationStatus(status, meta = {}) {
    const fullStatus = {
      ...status,
      init_check_attempts: meta.attempts,
      init_elapsed_ms: meta.elapsedMs
    }

    monitorAction("job_post.form.init_status", fullStatus)

    const failedComponents = this.failedComponents(fullStatus)
    if (failedComponents.length > 0) {
      monitorError("Job post form components failed to initialize", {
        ...fullStatus,
        failed_components: failedComponents.join(",")
      })
    }

    postFrontendDiagnostic("/frontend_diagnostics/job_post_form", {
      ...fullStatus,
      failed_components: failedComponents.join(",")
    })
  }

  failedComponents(status) {
    const components = []

    if (status.job_title_should_initialize && !status.tom_select_initialized) components.push("job_title")
    if (!status.photo_picker_initialized) components.push("photo_picker")
    if (status.ad_editor_should_initialize && !status.ad_editor_initialized) components.push("ad_content")
    if (status.how_to_apply_editor_should_initialize && !status.how_to_apply_editor_initialized) components.push("how_to_apply_content")

    return components
  }

  tomSelectInitialized() {
    const select = this.jobTitleSelect()
    if (!select) return false

    return !!select.tomselect
  }

  jobTitleSelectPresent() {
    return !!this.jobTitleSelect()
  }

  jobTitleSelectControllerAttached() {
    const select = this.jobTitleSelect()
    if (!select) return false

    const controllers = (select.dataset.controller || "").split(" ")
    return controllers.includes("tom-select")
  }

  jobTitleSelect() {
    return this.formTarget.querySelector("select[name='job_post[job_title]']")
  }

  photoPickerInitialized() {
    const photoItems = this.formTarget.querySelectorAll("[data-controller~='photo-select']")

    // No photo options means the picker intentionally doesn't render.
    if (photoItems.length === 0) return true

    const photoField = this.formTarget.querySelector("input[id$='_photo_id']")
    return !!photoField
  }

  editorInitialized(attribute) {
    const editor = this.editorElement(attribute)
    if (!editor) return false

    return !!editor.editor
  }

  editorPresent(attribute) {
    return !!this.editorElement(attribute)
  }

  editorElement(attribute) {
    const inputPattern = new RegExp(`_${attribute}(?:_[a-z]{2})?$`)

    return Array.from(this.formTarget.querySelectorAll("rhino-editor[input]"))
      .find(editor => inputPattern.test(editor.getAttribute("input")))
  }

  rhinoCustomElementDefined() {
    if (!window.customElements) return false

    return !!window.customElements.get("rhino-editor")
  }

  scriptFingerprint() {
    const scripts = Array.from(document.querySelectorAll("script[src]"))
      .map(script => script.getAttribute("src"))
      .filter(src => src && src.includes("application"))

    return scripts.join("|")
  }

  initializeJobSchedule() {
    if (this.hasJobScheduleRadioTarget) {
      const seasonalRadio = this.jobScheduleRadioTargets.find(radio =>
        radio.id && radio.id.includes('job_schedule_seasonal')
      )

      if (seasonalRadio && !seasonalRadio.checked && this.hasSeasonRowTarget) {
        this.seasonRowTargets.forEach(row => {
          row.style.display = "none"
        })
      }
    }
  }

  initializePostJobButton() {
    if (this.hasPostJobButtonTarget) {
      this.postJobButtonTarget.disabled = true
    }
  }

  disablePrimaryCategory() {
    if (this.hasPrimaryCategoryTarget) {
      this.disableCheckbox(this.primaryCategoryTarget)
    }
  }

  initializeNoneOfTheAbove() {
    // Deselect None of the Above if other job types are selected
    if (this.hasNoneOfTheAboveTarget && this.hasJobTypeCheckboxTarget) {
      const anyChecked = this.jobTypeCheckboxTargets.some(cb => cb.checked)
      if (anyChecked) {
        this.noneOfTheAboveTarget.checked = false
      }
    }
  }

  initializeCheckLimits() {
    if (this.hasCheckLimitTarget) {
      this.checkLimitTargets.forEach(container => {
        this.applyCheckLimit(container)
      })
    }
  }

  applyCheckLimit(container) {
    if (!container) return

    const checkboxes = container.querySelectorAll("input[type=checkbox]")
    const limit = parseInt(container.getAttribute("data-limit"), 10)
    const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length
    const overLimit = checkedCount >= limit

    checkboxes.forEach(cb => {
      if (!cb.checked) {
        cb.disabled = overLimit
      } else {
        cb.disabled = false
      }
    })
  }

  disableCheckbox(element) {
    if (!element) return

    const checkbox = element.querySelector("input[type=checkbox]")
    const label = element.querySelector("label")

    if (label) label.classList.add("text-stone-500")
    if (checkbox) {
      checkbox.checked = true
      checkbox.disabled = true
    }
    element.classList.add("disabled-field")
  }

  enableCheckbox(element) {
    if (!element) return

    const checkbox = element.querySelector("input[type=checkbox]")
    if (checkbox) {
      checkbox.checked = true
      checkbox.disabled = false
    }
  }

  scrollToAnchor(anchor) {
    const element = document.querySelector(anchor)
    if (element) {
      window.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth'
      })
    }
  }
}
